import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AiSparkleIcon } from "@/components/ai-suggestion/ai-sparkle-icon";
import type { RouterOutputs } from "@/trpc";

export type PdfFillSuggestion =
  RouterOutputs["organizationForm"]["getPdfFillSuggestions"]["suggestions"][number];

/**
 * PDF field names arrive as raw template identifiers ("state", "DOB") —
 * uppercase only the first letter so acronyms keep their casing.
 */
function formatFieldLabel(label: string): string {
  if (label.length === 0) {
    return label;
  }
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export interface PdfPrefillNoticeProps {
  /** The suggestions that were actually applied to the signing session. */
  suggestions: PdfFillSuggestion[];
  /** Fired each time the details popover opens (analytics hook). */
  onViewDetails?: () => void;
}

/**
 * Disclosure bar above the DocuSeal signing embed: tells the signer their
 * prefilled values came from automation over their own past submissions —
 * not typed in by the organization — with a popover listing every applied
 * field. Values land in DocuSeal before the signer sees the form, so this is
 * an attribution disclosure, not a suggestion affordance; that's why its
 * copy deliberately names "AI", unlike the inline suggestion primitives in
 * components/ai-suggestion/ (see docs/ai-suggestion-ui.md). Keep it in this
 * feature dir — it is not a reusable suggestion pattern.
 *
 * Host constraint: PdfSignEmbed must stay the sole child of the dialog
 * body's `overflow-auto` div — wrapping the embed in an intermediate div
 * would silently change what this bar's `sticky` binds to.
 */
export function PdfPrefillNotice({
  suggestions,
  onViewDetails,
}: PdfPrefillNoticeProps) {
  const count = suggestions.length;
  const fieldCountLabel = count === 1 ? "field" : "fields";

  // z-[60]: DocuSeal's embed has its own sticky top-0 z-50 elements (the
  // form header, and the sandbox banner in dev) that share this stacking
  // context and would paint over anything lower as the document scrolls.
  return (
    <div
      role="status"
      className="sticky top-0 z-[60] flex items-center justify-between gap-3 border-b border-purple-100 bg-purple-50 px-4 py-2 text-xs sm:text-sm"
    >
      <span className="flex items-center gap-1.5 text-purple-700">
        <AiSparkleIcon size={16} />
        {count} {fieldCountLabel} auto-filled by AI from your past submissions
      </span>
      <Popover
        onOpenChange={(open) => {
          if (open) {
            onViewDetails?.();
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="link"
            size="none"
            className="h-auto shrink-0 p-0 text-purple-700"
            aria-label={`View details of ${count} auto-filled ${fieldCountLabel}`}
          >
            View details
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="max-h-72 w-80 overflow-y-auto p-0"
        >
          <ul className="divide-y divide-purple-50">
            {suggestions.map((suggestion) => (
              <li key={suggestion.formFieldId} className="px-4 py-2.5">
                <p className="text-xs font-medium text-gray-500">
                  {formatFieldLabel(suggestion.fieldLabel)}
                </p>
                <p className="break-words text-sm font-medium text-gray-900">
                  {suggestion.value}
                </p>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
